import is from '@sindresorhus/is';
import { z } from 'zod';

import { type LanguageCodeForJSA, languageCodeSchemaForJSA } from '~/shared/internationalization';

import type { ProfileTaskParentContext } from '../model/profile-task';
import {
    isWholeOrNoProfileAddress,
    optionalProfileAddressShape,
    profileAddressMessage,
    segmentIdSchema,
    toProfileAddress,
} from './automation-tile-address';

export const JSA_AUTOMATION_ASSESSMENT_TILE_TYPE = 'v1.jsa.automation-assessment' as const;
export const AUTOMATION_ASSESSMENT_TILE_SLOT_KEY = 'jsa.automation-assessment';
export const AUTOMATION_ASSESSMENT_TILE_WIDTH = '500px';

/** Mirrors the task tile's address so the detail can re-read the same task. */
export const automationAssessmentTileSchema = z
    .object({
        ...optionalProfileAddressShape,
        language: languageCodeSchemaForJSA.optional(),
        taskId: segmentIdSchema,
        taskVersionId: z.string().trim().min(1).optional(),
        type: z.literal(JSA_AUTOMATION_ASSESSMENT_TILE_TYPE),
    })
    .refine(isWholeOrNoProfileAddress, { message: profileAddressMessage });

export type AutomationAssessmentTilePayload = z.infer<typeof automationAssessmentTileSchema>;

export const createAutomationAssessmentTilePayload = ({
    context,
    language,
    taskId,
    taskVersionId,
}: {
    /** The job profile the task is listed on; absent for a work task opened on its own. */
    readonly context?: ProfileTaskParentContext;
    readonly language?: LanguageCodeForJSA;
    readonly taskId: string;
    readonly taskVersionId?: string;
}): AutomationAssessmentTilePayload => ({
    ...toProfileAddress(context),
    ...(is.undefined(language) ? {} : { language }),
    taskId,
    ...(is.undefined(taskVersionId) ? {} : { taskVersionId }),
    type: JSA_AUTOMATION_ASSESSMENT_TILE_TYPE,
});
